import type { FleetPage, HubDetail, UpdatePolicy } from './types';

// Both tokens live in sessionStorage: the tab is the session, closing it ends
// it, and nothing here survives to another tab. localStorage would outlive the
// operator walking away from the machine, which for a tool that can decommission
// hardware is the wrong default.
const ACCESS_KEY = 'eeyoo-admin.access';
const REFRESH_KEY = 'eeyoo-admin.refresh';

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly body?: unknown) {
    super(message);
  }
}

export function storedAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_KEY);
}

export function storeTokens(access: string, refresh: string): void {
  sessionStorage.setItem(ACCESS_KEY, access);
  sessionStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens(): void {
  sessionStorage.removeItem(ACCESS_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
}

async function readError(response: Response): Promise<ApiError> {
  let body: unknown;
  let message = `${response.status} ${response.statusText}`;

  try {
    body = await response.json();
    const nested = body as { message?: unknown };
    if (typeof nested?.message === 'string') {
      message = nested.message;
    } else if (Array.isArray(nested?.message)) {
      // class-validator returns an array of failures; showing all of them is
      // more use to an operator than showing the first.
      message = nested.message.join('; ');
    }
  } catch {
    // A non-JSON body (a proxy error page, say) still deserves its status.
  }

  return new ApiError(response.status, message, body);
}

/**
 * Every request is same-origin and relative, in every environment. See
 * apps/eeyoo-admin/README.md — the server refuses cross-origin browser
 * requests, so an absolute API base here would work in exactly zero
 * deployments.
 *
 * A 401 buys exactly one refresh-and-retry. Looping would turn an expired
 * session into a request storm against the auth throttle.
 */
async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const token = storedAccessToken();

  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (response.status === 401 && retry && (await refreshSession())) {
    return request<T>(path, init, false);
  }

  if (!response.ok) {
    throw await readError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function refreshSession(): Promise<boolean> {
  const refresh = sessionStorage.getItem(REFRESH_KEY);

  if (!refresh) {
    return false;
  }

  const response = await fetch('/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refresh }),
  });

  if (!response.ok) {
    clearTokens();
    return false;
  }

  const tokens = (await response.json()) as { access_token: string; refresh_token: string };
  storeTokens(tokens.access_token, tokens.refresh_token);
  return true;
}

export async function requestLoginCode(email: string): Promise<void> {
  const response = await fetch('/auth/request-login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw await readError(response);
  }
}

export async function login(email: string, token: string): Promise<void> {
  const response = await fetch('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, token }),
  });

  if (!response.ok) {
    throw await readError(response);
  }

  const tokens = (await response.json()) as { access_token: string; refresh_token: string };
  storeTokens(tokens.access_token, tokens.refresh_token);
}

export interface FleetFilters {
  status?: string;
  channel?: string;
  q?: string;
  decommissioned?: boolean;
  limit?: number;
  offset?: number;
}

export function fetchFleet(filters: FleetFilters): Promise<FleetPage> {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '' && value !== false) {
      params.set(key, String(value));
    }
  }

  const query = params.toString();
  return request<FleetPage>(`/admin/fleet${query ? `?${query}` : ''}`);
}

export function fetchHub(hubId: string): Promise<HubDetail> {
  return request<HubDetail>(`/admin/fleet/${encodeURIComponent(hubId)}`);
}

/**
 * `expected` is what the detail view was showing when the operator decided.
 * The server refuses the write if the row has moved since, which is why it is
 * a required argument here rather than something this client may omit.
 */
export function setUpdatePolicy(
  hubId: string,
  change: { channel?: string | null; pinnedReleaseId?: string | null },
  expected: UpdatePolicy,
): Promise<unknown> {
  return request(`/admin/fleet/${encodeURIComponent(hubId)}/update-policy`, {
    method: 'PATCH',
    body: JSON.stringify({ ...change, expected }),
  });
}

export function updateNow(hubId: string): Promise<{ deduplicated: boolean }> {
  return request(`/admin/fleet/${encodeURIComponent(hubId)}/update-now`, { method: 'POST' });
}

export function decommission(hubId: string, confirmHubId: string): Promise<unknown> {
  return request(`/admin/fleet/${encodeURIComponent(hubId)}/decommission`, {
    method: 'POST',
    body: JSON.stringify({ confirmHubId }),
  });
}

export function recommission(hubId: string, confirmHubId: string): Promise<unknown> {
  return request(`/admin/fleet/${encodeURIComponent(hubId)}/recommission`, {
    method: 'POST',
    body: JSON.stringify({ confirmHubId }),
  });
}
